// Backend
import React from 'react';
import axios from '../../../helpers/AxiosInstance';

// Frontend
import {
  ChevronUp,
  ChevronDown,
  Download,
  Save,
  Check2Circle,
  PlusCircle,
  Plus,
  X
} from 'react-bootstrap-icons';
import { FaInfoCircle } from 'react-icons/fa';
import { Alert, Modal } from 'react-bootstrap';
import { Grid, Cell } from 'react-foundation';
import '../../../stylesheets/components/conversations/Canvas.css';
import { Tooltip } from 'react-tooltip';

// Functionality
import { useParams, useLocation, Link, useNavigate } from 'react-router-dom';

// Components
import {
  DluchApi,
  FlashMessage,
  Flashable,
  ProgressBar,
  ErrorRender,
  setToken,
  filteredCategories,
} from '../../../constants/SharedComponents';

import useCurrentUser from '../../../hooks/useCurrentUser';
import useWindowDimensions from '../../../hooks/useWindowDimensions';

// ReactFlow - core
import {
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  useNodesState,
  useEdgesState,
  Controls,
  MiniMap,
  reconnectEdge
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import Sidebar from '../../../components/ReactFlow/Sidebar';

// ReactFlow - nodes
import ImpactNode from '../../../components/ReactFlow/ImpactNode';
import CategoryNode from '../../../components/ReactFlow/CategoryNode';
import WhyNode from '../../../components/ReactFlow/WhyNode';
import { HashLink } from 'react-router-hash-link';
import ButtonEdge from '../../../components/ReactFlow/ButtonEdge';
import CustomLine from '../../../components/ReactFlow/ConnectionLine';

const initialNodes = [];

const nodeTypes = {
  impactNode: ImpactNode,
  categoryNode: CategoryNode,
  whyNode: WhyNode,
};

const edgeTypes = {
  buttonedge: ButtonEdge,
};

const defaultEdgeOptions = {
  label: 'Custom Edge',
  type: 'buttonedge'
}

let id = 0;
const defaultViewport = { x: 0, y: 0, zoom: 0.75 };
const getId = (nodeType) => `${nodeType}--${id++}`;
const snapGrid = [25, 25];

const nodeColor = (node) => {
  switch (node.type) {
    case 'impactNode':
      return '#E65722'; // Brand orange
    case 'categoryNode':
      return '#008A1E'; // Brand green
    case 'whyNode':
      return '#007acc'; // Blue-500
    default:
      return '#007acc'; 
  }
};

export function CanvasesCategoriesEdit() {
  // Params
  const { conversationId, categoryId } = useParams();
  const categoryIndex = parseInt(categoryId);

  // API
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const { state } = useLocation();
  const token = setToken(currentUser);
  const headerParams = { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 };

  // Objects
  const [inputs, setInputs] = React.useState({});
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [flashMessageObject, setFlashMessageObject] = React.useState({});
  const [loading, setLoading] = React.useState(true);
  const [categoriesData, setCategoriesData] = React.useState(null);
  const [categoryData, setCategoryData] = React.useState(null);
  const [canvasData, setCanvasData] = React.useState({});
  const [submissionUnlocked, setSubmissionUnlocked] = React.useState(false);
  const [newRecord, setNewRecord] = React.useState(false);
  const [controlsCount, setControlsCount] = React.useState(1);
  const [actionsCount, setActionsCount] = React.useState({ 0: 1 });
  const [canNavigate, setCanNavigate] = React.useState(true);

  const [extraTabs, setExtraTabs] = React.useState(0);

  const extraTabsMaxLimit = 1;
  const numberOfMainTabs = 3;

  const [formChanged, setFormChanged] = React.useState(false);

  const [controlsAndActionsAreaClassName, setControlsAndActionsAreaClassName] =
    React.useState('hide');
  const [controlsAndActionsButtonClassName, setControlsAndActionsButtonClassName] =
    React.useState('');

  const [saveAlert, setSaveAlert] = React.useState(false);
  const handleCloseSaveAlert = () => {
    setSaveAlert(false);
  }

  const [deleteControlModal, setDeleteControlModal] = React.useState(undefined);

  const [deleteTabModal, setDeleteTabModal] = React.useState(undefined);

  const { width } = useWindowDimensions();

  const [zoomToggle, setZoomToggle] = React.useState(true);

  // ReactFlow - core
  const reactFlowWrapper = React.useRef(null);
  const [nodes, setNodes] = useNodesState(initialNodes);
  const [edges, setEdges] = useEdgesState([]);
  const [reactFlowInstance, setReactFlowInstance] = React.useState();

  // Autosave functionality
  const timeoutRef = React.useRef(null);

  const reactFlowInstanceRef = React.useRef(reactFlowInstance);
  const newRecordRef = React.useRef(newRecord);
  const submissionUnlockedRef = React.useRef(submissionUnlocked);
  const canvasDataRef = React.useRef(canvasData);
  const inputsRef = React.useRef(inputs);
  const currentUserRef = React.useRef(currentUser);

  const autoSaveIntervalMs = 5 * 60 * 1000;

  function handleOpenDeleteControlModal(e, id) {
    e.preventDefault();
    setDeleteControlModal(id);
  }
  function handleCloseDeleteControlModal(e) {
    e?.preventDefault();
    setDeleteControlModal(false);
  }

  function handleOpenDeleteTabModal(e, id) {
    e.preventDefault();
    setDeleteTabModal(id);
    setCanNavigate(false);
  }
  function handleCloseDeleteTabModal(e) {
    e.preventDefault();
    setDeleteTabModal(undefined);
    setCanNavigate(true);
  }

  const [deleteActionModal, setDeleteActionModal] = React.useState(undefined);
  function handleOpenDeleteActionModal(e, id) {
    e.preventDefault();
    setDeleteActionModal(id);
  }
  function handleCloseDeleteActionModal(e) {
    e?.preventDefault();
    setDeleteActionModal(false);
  }


  const controlsAndActionsAreaWrapper = React.useRef(null);
  const controlsAndActionsButtonWrapper = React.useRef(null);

  // Functions
  function createSubmission(
    isFinished,
    newCanvas,
    reactFlowInstance,
    formInputs,
    canvasData,
    redirect,
  ) {
    setLoading(true);
    let Api = new DluchApi('ConversationDataEdit');

    const flow = reactFlowInstance.toObject();
    const flowData = JSON.stringify(flow);

    canvasData[categoryIndex] = { flowData: flowData, additionalInput: JSON.stringify(formInputs) };

    let requestPayload = {
      conversationId: conversationId,
      canvasData: JSON.stringify(canvasData),
      isFinished: isFinished,
    };

    if (newCanvas) {
      axios
        .post(Api.ApiRequestUrl(), requestPayload, headerParams)
        .then(() => {
          setSaveAlert(true);
          setLoading(false);
          setNewRecord(false);
          newRecordRef.current = false;
          setFormChanged(false);

          if (isFinished) {
            setSubmissionUnlocked(false);
            submissionUnlockedRef.current = false;
          }

          if (redirect) {
            navigate(redirect);
          }
        })
        .catch((error) => {
          if (error.response?.data) {
            setFlashMessageObject(Flashable(error.response.data.errorDesc, 'alert', false));
          } else {
            setFlashMessageObject(Flashable(error.message, 'alert', false));
          }

          setLoading(false);
        });
    } else {
      axios
        .put(Api.ApiRequestUrl(), requestPayload, headerParams)
        .then(() => {
          setSaveAlert(true);
          setLoading(false);
          setFormChanged(false);

          if (isFinished) {
            setSubmissionUnlocked(false);
            submissionUnlockedRef.current = false;
          }

          if (redirect) {
            navigate(redirect);
          }
        })
        .catch((error) => {
          setFlashMessageObject(Flashable(error.response.data.errorDesc, 'alert', false));
          setLoading(false);
        });
    }
  }

  function createAutoSubmission(
    isFinished,
    newCanvas,
    reactFlowInstance,
    formInputs,
    canvasData,
    redirect
  ) {
    setLoading(true);
    let Api = new DluchApi('ConversationDataEdit');

    const flow = reactFlowInstance.toObject();
    const flowData = JSON.stringify(flow);

    canvasData[categoryIndex] = { flowData: flowData, additionalInput: JSON.stringify(formInputs) };

    let requestPayload = {
      conversationId: conversationId,
      canvasData: JSON.stringify(canvasData),
      isFinished: isFinished,
    };

    const currentToken = setToken(currentUserRef.current);
    const currentHeaderParams = { headers: { Authorization: `Bearer ${currentToken}` }, timeout: 30000 };

    if (newCanvas) {
      axios
        .post(Api.ApiRequestUrl(), requestPayload, currentHeaderParams)
        .then(() => {
          setSaveAlert(true);
          setLoading(false);
          setNewRecord(false);
          newRecordRef.current = false;
          setFormChanged(false);

          if (isFinished) {
            setSubmissionUnlocked(false);
            submissionUnlockedRef.current = false;
          }

          if (redirect) {
            navigate(redirect);
          }
        })
        .catch((error) => {
          if (error.response?.data) {
            setFlashMessageObject(Flashable(error.response.data.errorDesc, 'alert', false));
          } else {
            setFlashMessageObject(Flashable(error.message, 'alert', false));
          }

          setLoading(false);
        });
    } else {
      axios
        .put(Api.ApiRequestUrl(), requestPayload, currentHeaderParams)
        .then(() => {
          setSaveAlert(true);
          setLoading(false);
          setFormChanged(false);

          if (isFinished) {
            setSubmissionUnlocked(false);
            submissionUnlockedRef.current = false;
          }

          if (redirect) {
            navigate(redirect);
          }
        })
        .catch((error) => {
          setFlashMessageObject(Flashable(error.response.data.errorDesc, 'alert', false));
          setLoading(false);
        });
    }
  }

  function createDeleteTabSubmission(
    isFinished,
    newCanvas,
    canvasData,
    redirect,
  ) {
    setLoading(true);
    let Api = new DluchApi('ConversationDataEdit');

    let requestPayload = {
      conversationId: conversationId,
      canvasData: JSON.stringify(canvasData),
      isFinished: isFinished,
    };

    if (newCanvas) {
      axios
        .post(Api.ApiRequestUrl(), requestPayload, headerParams)
        .then(() => {
          setSaveAlert(true);
          setLoading(false);
          setNewRecord(false);
          newRecordRef.current = false;
          setFormChanged(false);

          if (isFinished) {
            setSubmissionUnlocked(false);
            submissionUnlockedRef.current = false;
          }

          if (redirect) {
            navigate(redirect);
          }
        })
        .catch((error) => {
          if (error.response?.data) {
            setFlashMessageObject(Flashable(error.response.data.errorDesc, 'alert', false));
          } else {
            setFlashMessageObject(Flashable(error.message, 'alert', false));
          }

          setLoading(false);
        });
    } else {
      axios
        .put(Api.ApiRequestUrl(), requestPayload, headerParams)
        .then(() => {
          setSaveAlert(true);
          setLoading(false);
          setFormChanged(false);

          if (isFinished) {
            setSubmissionUnlocked(false);
            submissionUnlockedRef.current = false;
          }

          if (redirect) {
            navigate(redirect);
          }
        })
        .catch((error) => {
          setFlashMessageObject(Flashable(error.response.data.errorDesc, 'alert', false));
          setLoading(false);
        });
    }
  }

  // ReactFlow - functionality
  const onNodesChange = React.useCallback(
    (changes) => {
      resetAutoSaveTimer();
      setNodes((nds) => applyNodeChanges(changes, nds));
    },
    [setNodes],
  );

  const onEdgesChange = React.useCallback(
    (changes) => {
      resetAutoSaveTimer();
      setEdges((eds) => applyEdgeChanges(changes, eds));
    },
    [setEdges],
  );

  const onInit = React.useCallback((reactFlowInstance) => {
    setReactFlowInstance(reactFlowInstance);
    reactFlowInstanceRef.current = reactFlowInstance;
  }, []);

  const onConnect = React.useCallback((params) => setEdges((eds) => addEdge(params, eds)), [setEdges]);

  const onReconnect = React.useCallback(
    (oldEdge, newConnection) =>
      setEdges((els) => reconnectEdge(oldEdge, newConnection, els)),
    [],
  );

  const onSave = React.useCallback(() => {
    createSubmission(!submissionUnlocked, newRecord, reactFlowInstance, inputs, canvasData, null);
  }, [reactFlowInstance, newRecord, inputs, canvasData, submissionUnlocked]);

  const onAutoSave = React.useCallback(() => {
    createAutoSubmission(!submissionUnlockedRef.current, newRecordRef.current, reactFlowInstanceRef.current, inputsRef.current, canvasDataRef.current, null);
  }, [reactFlowInstanceRef, newRecordRef, inputsRef, canvasDataRef, submissionUnlockedRef]);

  const saveBeforeNavigation = React.useCallback(
    (event) => {
      if (submissionUnlocked && canNavigate) {
        event.preventDefault();
        let to = event.target.getAttribute('to');
        if (!to) {
          to = event.target.getAttribute('href');
        }
        createSubmission(false, newRecord, reactFlowInstance, inputs, canvasData, to);
        setFormChanged(false);
      } else if (!submissionUnlocked && canNavigate) {
        // For completed conversations, allow navigation without saving
        event.preventDefault();
        let to = event.target.getAttribute('to');
        if (!to) {
          to = event.target.getAttribute('href');
        }
        navigate(to);
      }
    },
    [reactFlowInstance, newRecord, inputs, canvasData, submissionUnlocked, canNavigate, navigate],
  );

  const saveAndCompleteConversation = React.useCallback(
    (event) => {
      if (submissionUnlocked && canNavigate) {
        event.preventDefault();
        const to = event.target.getAttribute('to');

        // Save the canvas data with isFinished: true to complete the conversation
        setLoading(true);
        let canvasApi = new DluchApi('ConversationDataEdit');

        const flow = reactFlowInstance.toObject();
        const flowData = JSON.stringify(flow);

        canvasData[categoryIndex] = { flowData: flowData, additionalInput: JSON.stringify(inputs) };

        let canvasPayload = {
          conversationId: conversationId,
          canvasData: JSON.stringify(canvasData),
          isFinished: true, // This should mark the conversation as completed
        };

        const saveCanvas = newRecord ?
          axios.post(canvasApi.ApiRequestUrl(), canvasPayload, headerParams) :
          axios.put(canvasApi.ApiRequestUrl(), canvasPayload, headerParams);

        saveCanvas
          .then(() => {
            // Canvas saved successfully with isFinished: true
            setSaveAlert(true);
            setLoading(false);
            setNewRecord(false);
            newRecordRef.current = false;
            setFormChanged(false);
            setSubmissionUnlocked(false);
            submissionUnlockedRef.current = false;

            const flow = JSON.parse(canvasData[categoryIndex].flowData);

            for (let row of flow.nodes) {
              row.data.locked = true;
            }

            setNodes(flow.nodes);

            if (to) {
              navigate(to);
            }
          })
          .catch((error) => {
            if (error.response?.data) {
              setFlashMessageObject(Flashable(error.response.data.errorDesc, 'alert', false));
            } else {
              setFlashMessageObject(Flashable(error.message, 'alert', false));
            }
            setLoading(false);
          });
      }
    },
    [reactFlowInstance, newRecord, inputs, canvasData, submissionUnlocked, canNavigate, conversationId, categoryIndex, headerParams, navigate],
  );

  const addTopicTab = React.useCallback(
    (event) => {
      if (submissionUnlocked && extraTabs < extraTabsMaxLimit) {
        event.preventDefault();

        let currentExtraTabs = extraTabs + 1;
        let newTabNumber = currentExtraTabs + numberOfMainTabs;

        let flow = {
          nodes: [
            {
              id: 'categoryNode',
              type: 'categoryNode',
              deletable: false,
              position: { x: 0, y: 0 },
              data: {
                title: `Topic ${newTabNumber}`,
                value: '',
                startingWidth: 0
              },
            },
          ],
          edges: []
        };

        let newNodeData = {
          flowData: JSON.stringify(flow),
          additionalInput: "{}"
        }

        let canvasDataCopy = canvasData;
        canvasDataCopy[newTabNumber] = newNodeData;

        setCanvasData(canvasDataCopy);
        canvasDataRef.current = canvasDataCopy;

        setExtraTabs(currentExtraTabs);
      }
    },
    [extraTabs, extraTabsMaxLimit, submissionUnlocked, canvasData, numberOfMainTabs],
  );

  const removeTopicTab = React.useCallback(
    (event) => {
      if (submissionUnlocked && extraTabs > 0) {
        event.preventDefault();

        const topicIndex = event.target.getAttribute('data-tab-count');
        let id = `canvas-category-list-link--${topicIndex - 1}`;
        let needNavigate = false;

        if (!document.getElementById(id)) {
          needNavigate = true;
          id = `canvas-category-selected-list-link--${topicIndex - 1}`;
        }

        document.getElementById(id).parentElement.style = 'display:none';

        let newExtraCount = extraTabs - 1;

        setExtraTabs(newExtraCount);

        let newCanvasData = canvasData;
        delete newCanvasData[topicIndex];

        // Fix keys with values higher than this index:
        let maxKey = Math.max(...Object.keys(newCanvasData).map(Number));
        for (let i = topicIndex + 1; i <= maxKey; i++) {
          if (newCanvasData) {
            newCanvasData[i - 1] = newCanvasData[i];
            delete newCanvasData[i];
          }
        }

        setCanvasData(newCanvasData);
        canvasDataRef.current = newCanvasData;

        if (needNavigate) {
          let navTo = `/canvases/${data.id}/categories/1`;
          event.preventDefault();
          createDeleteTabSubmission(false, newRecord, newCanvasData, navTo);
          setFormChanged(false);
        }

        setDeleteTabModal(false);
        setCanNavigate(true);
      }
    },
    [extraTabs, canvasData, submissionUnlocked, newRecord],
  );


  const toggleControlsAndActions = React.useCallback(
    (event) => {
      const href = event.target.getAttribute('href');
      event.preventDefault();

      if (controlsAndActionsAreaClassName === 'hide') {
        setControlsAndActionsButtonClassName('hide');
        setControlsAndActionsAreaClassName('');
      } else {
        setControlsAndActionsButtonClassName('');
        setControlsAndActionsAreaClassName('hide');
      }

      if (href.includes('#')) {
        const id = `${href.substring(href.indexOf('#') + 1)}`;
        if (id) {
          setTimeout(
            function () {
              const anchor = document.getElementById(id);
              anchor.scrollIntoView({ behavior: 'smooth' });
            },
            250,
            id,
          );
        }
      }
    },
    [controlsAndActionsAreaClassName],
  );

  const addControl = React.useCallback(
    (event) => {
      event.preventDefault();
      let currentControlsCount = controlsCount + 1;

      const currentActionsCount = { ...actionsCount };
      currentActionsCount[controlsCount] = 1;

      setActionsCount(currentActionsCount);
      setControlsCount(currentControlsCount);
    },
    [controlsCount, actionsCount],
  );

  const addAction = React.useCallback(
    (event) => {
      event.preventDefault();

      const currentCount = { ...actionsCount };
      currentCount[event.target.getAttribute('data-controls-count')] =
        actionsCount[event.target.getAttribute('data-controls-count')] + 1;

      setActionsCount(currentCount);
    },
    [actionsCount],
  );

  const removeAction = React.useCallback(
    (event) => {
      event.preventDefault();

      const controlCount = event.target.getAttribute('data-controls-count');
      const actionCount = event.target.getAttribute('data-actions-count');

      let id = `canvas-category-actions--${controlCount}-${actionCount}`;
      let checkboxId = `checkbox--canvas-category-actions--${controlCount}-${actionCount}`;

      // Create a shallow copy of inputs to avoid mutating state directly
      let newInputs = { ...inputs };
      newInputs[id] = '';
      newInputs[checkboxId] = '';

      setDeleteActionModal(false);
      setInputs(newInputs);
      inputsRef.current = newInputs;
    },
    [inputs],
  );

  const removeControl = React.useCallback(
    (event) => {
      event.preventDefault();

      const controlCount = event.target.getAttribute('data-controls-count');
      const actionCount = event.target.getAttribute('data-actions-count');

      let id = `canvas-category-controls--${controlCount}`;

      // Create a shallow copy of inputs to avoid mutating state directly
      let newInputs = { ...inputs };
      newInputs[id] = '';

      // Remove all actions and checkboxes for this control
      for (let actionsIndex = 0; actionsIndex < actionCount; actionsIndex++) {
        let action = `canvas-category-actions--${controlCount}-${actionsIndex}`;
        let actionCheckbox = `checkbox--canvas-category-actions--${controlCount}-${actionsIndex}`;
        newInputs[action] = '';
        newInputs[actionCheckbox] = '';
      }

      setDeleteControlModal(false);
      setInputs(newInputs);
      inputsRef.current = newInputs;
    },
    [inputs],
  );

  const onDragOver = React.useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = React.useCallback(
    (event) => {
      resetAutoSaveTimer();
      setFormChanged(true);
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');

      // check if the dropped element is valid
      if (typeof type === 'undefined' || !type) {
        return;
      }

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newNode = {
        id: getId(type),
        type,
        position,
        data: { label: `${type} node` },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, setNodes],
  );

  // @see https://github.com/wbkd/react-flow/blob/afa38727fad2400e817a3dcc6c575cb83cbf2bd2/examples/vite-app/src/examples/UseReactFlow/index.tsx
  const onNodeClick = React.useCallback(
    (event, element) => {
      if (
        event.target.className === 'js--delete-node conversation-canvas-node__button' &&
        submissionUnlocked
      ) {
        reactFlowInstance.deleteElements({
          nodes: [{ id: element.id }],
          edges: [],
        });
      }
    },
    [reactFlowInstance, submissionUnlocked],
  );

  // Functions
  const getData = async () => {
    setLoading(true);

    try {
      let Api = new DluchApi('ConversationsShow', conversationId);
      const response = await axios.get(Api.ApiRequestUrl(), headerParams);
      let responseData = response.data.content.conversation;
      setData(responseData);
      setError(false);
      getQuestionCategoriesData(responseData);
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const getQuestionCategoriesData = async (conversationData) => {
    setLoading(true);

    try {
      let Api = new DluchApi('QuestionsCategoriesIndex', conversationId);
      const response = await axios.get(Api.ApiRequestUrl(), headerParams);
      let responseData = response.data.content.categories;
      let allCategories = filteredCategories(responseData, 3, false);
      setError(false);
      getQuestionResultsData(allCategories, conversationData);
    } catch (err) {
      setError(err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const getCanvasData = async (category = undefined) => {
    setLoading(true);

    let categoryTitle =
      category !== undefined && !category.selfGuided && category.canShareWithManager
        ? `Topic ${categoryIndex}: ${category.title}`
        : `Topic ${categoryIndex}`;

    if (category !== undefined && category.selfGuided) {
      categoryTitle = `Topic ${categoryIndex}: ${category.title}`;
    }

    let flow;
    const defaultNodes = {
      nodes: [
        {
          id: 'categoryNode',
          type: 'categoryNode',
          deletable: false,
          position: { x: 0, y: 0 },
          data: {
            title: categoryTitle,
            value: '',
            startingWidth: 0
          },
        },
      ],
      edges: []
    };

    try {
      let Api = new DluchApi('ConversationDataShow', conversationId);
      const response = await axios.get(Api.ApiRequestUrl(), headerParams);
      let responseData = response.data.content.data;
      let canvasData = JSON.parse(responseData.canvasData);
      let locked = responseData.isFinished;
      let defaultInputs = {};

      let numberOfCanvasTopics = Math.max(...Object.keys(canvasData));

      if (numberOfCanvasTopics > numberOfMainTabs) {
        let lengthDifference = numberOfCanvasTopics - numberOfMainTabs;
        setExtraTabs(lengthDifference);
      }

      if (locked) {
        setSubmissionUnlocked(false);
        submissionUnlockedRef.current = false;
      } else {
        setSubmissionUnlocked(true);
        submissionUnlockedRef.current = true;
      }

      if (canvasData.hasOwnProperty(categoryIndex)) {
        const flow = JSON.parse(canvasData[categoryIndex].flowData);
        let additionalInput = JSON.parse(canvasData[categoryIndex].additionalInput);

        if (locked) {
          for (let row of flow.nodes) {
            row.data.locked = locked;
          }
        }

        for (const [key, value] of Object.entries(additionalInput)) {
          defaultInputs[key] = value;
        }

        setEdges(flow.edges);
        setNodes(flow.nodes);
        setInputs(defaultInputs);
        inputsRef.current = defaultInputs;

        let controlsCount = 0;
        let actionsCountObject = {};

        if (defaultInputs) {
          for (let row of Object.keys(defaultInputs)) {
            if (row.includes('controls--')) {
              let value = row.split('--')[1];

              if (value > controlsCount) {
                controlsCount = parseInt(value);
              }
            }

            if (row.includes('actions--') && !(row.includes('checkbox--'))) {
              let values = row.split('--')[1].split('-');
              let control = parseInt(values[0]);
              let actionsCountTotal = parseInt(values[1]);

              if (
                actionsCount[control] === undefined ||
                actionsCountTotal > actionsCount[control]
              ) {
                actionsCount[control] = actionsCountTotal;
              }
            }

          }

          for (const [key, value] of Object.entries(actionsCount)) {
            actionsCountObject[key] = value + 1;
          }

          setControlsCount(controlsCount + 1);
          setActionsCount(actionsCountObject);
          setControlsAndActionsButtonClassName('hide');
          setControlsAndActionsAreaClassName('');
        }
      } else {
        setNodes(defaultNodes.nodes);
      }

      setCanvasData(canvasData);
      canvasDataRef.current = canvasData;

      setError(false);
    } catch {
      setNewRecord(true);
      newRecordRef.current = true;

      setSubmissionUnlocked(true);
      submissionUnlockedRef.current = true;

      flow = {};
      flow[categoryIndex] = defaultNodes;

      setCanvasData(flow);
      canvasDataRef.current = flow;

      setNodes(flow[categoryIndex].nodes);
    } finally {
      setLoading(false);
    }
  };

  const getQuestionResultsData = async (questionCategories, conversationData) => {
    setLoading(true);

    if (conversationData.employeePrepStatus === 2 || (conversationData.isCanvasData && conversationData.employeePrepStatus !== 1)) {
      try {
        let Api = new DluchApi('QuestionResultsShow', conversationId);
        const response = await axios.get(Api.ApiRequestUrl(), headerParams);
        let responseData = response.data.content.results.filter(
          (obj) => obj.isConversationTopic === true,
        );

        let guidedOptionalCategories = filteredCategories(questionCategories, 1);
        guidedOptionalCategories = guidedOptionalCategories.map((obj) => obj.id);

        let selfGuidedCategories = filteredCategories(questionCategories, 2);
        selfGuidedCategories = selfGuidedCategories.map((obj) => obj.id);

        for (let row of questionCategories) {
          row.selfGuided =
            guidedOptionalCategories.includes(row.id) || selfGuidedCategories.includes(row.id);
        }

        for (let row of responseData) {
          row.title = row.optionalCategoryName === null ? row.categoryName : row.optionalCategoryName;
          let category = questionCategories.find((obj) => obj.id === row.categoryId);

          row.selfGuided = false;

          if (category) {
            row.selfGuided = category.selfGuided;
          }
        }
        setCategoriesData(responseData);
        let category = responseData[categoryIndex - 1];
        setCategoryData(category);
        setError(false);

        getCanvasData(category);
      } catch {
        setError(false);
        getCanvasData();
      } finally {
        setLoading(false);
      }
    }
    else {
      setError(false);
      getCanvasData();
      setLoading(false);
    }
  };

  function isViewable(data, submissionUnlocked) {
    return (data.isUserManager && submissionUnlocked) || !submissionUnlocked;
  }

  function isValidConnection(connection) {
    return connection.source !== connection.target;
  }

  function retryFetch() {
    setLoading(true);
    setError(false);
    setData(null);
    getData();
  }

  const handleChange = (event) => {
    resetAutoSaveTimer();

    const name = event.target.name;
    let value = event.target.value;

    if (
      event.target.className.includes('canvas-category-controls-and-actions-section-item__input')
    ) {
      event.target.style.height = 0;
      event.target.style.height = `${event.target.scrollHeight + 5}px`;

      if (event.target.value === '') {
        value = ' '
      }
    }

    if (name.includes('checkbox--canvas-category-actions--')) {
      value = event.target.checked;
    }

    setInputs((values) => {
      const updated = { ...values, [name]: value };
      inputsRef.current = updated;
      return updated;
    });
  };

  const [isHover, setIsHover] = React.useState(false);

  const [showExitModal, setShowExitModal] = React.useState(false);
  const [nextRoute, setNextRoute] = React.useState(null);

  const handleNavClick = (href) => {
    if (formChanged) {
      setNextRoute(href);
      setShowExitModal(true);
    } else {
      navigate(href);
    }
  };

  const handleSaveAndContinue = () => {
    onSave();
    setShowExitModal(false);
    setFormChanged(false);
    if (nextRoute) navigate(nextRoute);
  };

  const handleDiscardAndContinue = () => {
    setShowExitModal(false);
    setFormChanged(false);
    if (nextRoute) navigate(nextRoute);
  };

  const handleCancelExit = () => {
    setShowExitModal(false);
    setNextRoute(null);
  };

  const handleMouseEnter = () => {
    setIsHover(true);
  };
  const handleMouseLeave = () => {
    setIsHover(false);
  };

  const handleEdgeMouseEnter = (e, edge) => {
    if (submissionUnlocked) {
      setEdges((edges) =>
        edges.map((edg) => {
          if (edg.id === edge.id) {
            return { ...edg, className: "change-edge", animated: true };
          } else {
            return edg;
          }
        })
      );
    }

    let element = document.getElementById(`${edge.id}-edgebutton`);

    if (element) {
      element.classList.remove('d-none');
    }
  };

  const handleEdgeMouseLeave = (e, edge) => {
    if (submissionUnlocked) {
      setEdges((edges) =>
        edges.map((edg) => {
          if (edg.id === edge.id) {
            return { ...edg, className: {}, animated: false };
          } else {
            return edg;
          }
        })
      );

      let element = document.getElementById(`${edge.id}-edgebutton`);

      if (element) {
        element.classList.add('d-none');
      }
    }
  };

  const handleScrollWheel = (event) => {
    const isCtrlPressed = event.ctrlKey;

    if (!isCtrlPressed) {
      event.stopPropagation();
      setZoomToggle(false);
    }
    else {
      setZoomToggle(true);
    }
  }

  const resetAutoSaveTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      onAutoSave();
    }, autoSaveIntervalMs);
  }

  React.useEffect(() => {
    if (currentUser?.token) {
      retryFetch();
      currentUserRef.current = currentUser;
    }

    if (state?.hasOwnProperty('flash')) {
      setFlashMessageObject(state.flash);
    }

    return () => {
      if (timeoutRef.current)
        clearTimeout(timeoutRef.current);
    }
  }, [currentUser, state]);

  return (
    <div className="canvas-content-wrap">
      <ProgressBar loading={loading} />
      <FlashMessage
        object={flashMessageObject}
        columnWrap={true}
        className="flash-message-compact"
      />
      {error && (
        <ErrorRender object={error} />
      )}

      {data && (
        <>
          {!data.isUserManager && submissionUnlocked && categoryData && (
            <div className="grid-container">
              <Grid className="grid-padding-x">
                <Cell small={12}>
                  <div className="page-content">
                    <h1 className="page-content__title">Canvas in progress</h1>

                    <div className="page-content__styles">
                      <p>
                        This canvas is not yet complete. You will be able to see this when it is
                        finished.
                      </p>
                    </div>
                  </div>
                </Cell>
              </Grid>
            </div>
          )}

          {isViewable(data, submissionUnlocked) && (
            <div className="page-content">
              <div className="page-content__header page-content__header--bordered page-content__header--fixed">

                <div className='button-container'>
                  <div className='button-set'>
                    <button
                      type="button"
                      className="page-content__button--medium page-content__button--back page-content__button--inline"
                      id="page-navigation-button--go-back"
                      onClick={() => handleNavClick('/conversations')}
                    >
                      Back to Dashboard
                    </button>
                    <button
                      type="button"
                      className="page-content__button--medium page-content__button--secondary"
                      id="page-navigation-button--go-back"
                      onClick={() => window.open('/resources', '_blank', 'noopener,noreferrer')}
                    >
                      Resources
                    </button>
                  </div>

                  <div className='button-set'>
                    <ul className="canvas-categories-navigation-list">
                      {submissionUnlocked && (
                        <li className="canvas-categories-navigation-list__item">
                          <button
                            onClick={onSave}
                            id="page-navigation-button--save"
                            className="page-content__button--medium"
                          >
                            Save for later{''}
                            <span className="canvas-categories-navigation-list__icon">
                              <Save size={14} />
                            </span>
                          </button>
                        </li>
                      )}

                      {!submissionUnlocked && (
                        <li className="canvas-categories-navigation-list__item">
                          <button
                            onClick={onSave}
                            id="page-navigation-button--update-save"
                            className="page-content__button--medium page-content__button--inline"
                          >
                            Update{''}
                            <span className="canvas-categories-navigation-list__icon">
                              <Save size={14} />
                            </span>
                          </button>
                        </li>
                      )}

                      {submissionUnlocked && (
                        <li className="canvas-categories-navigation-list__item">
                          <Link
                            to={`/canvases/${data.id}/confirmations/new`}
                            id="page-navigation-button--complete"
                            className="page-content__button--medium"
                            onClick={saveAndCompleteConversation}
                          >
                            Save and finish{''}
                            <span className="canvas-categories-navigation-list__icon">
                              <Check2Circle size={14} />
                            </span>
                          </Link>
                        </li>
                      )}

                      {!submissionUnlocked && (
                        <li className="canvas-categories-navigation-list__item">
                          <Link
                            to={`/canvases/${data.id}/exports/new`}
                            id="page-navigation-button--download"
                            className="page-content__button--medium page-content__button--inline"
                            onClick={saveBeforeNavigation}
                          > Download PDF{''}
                            <span className="canvas-categories-navigation-list__icon"><Download size={14} /></span>
                          </Link>
                        </li>
                      )}
                    </ul>

                  </div>
                </div>

                <Grid>
                  <Cell large={12} className="text-center">
                    <ul className="canvas-categories-list">
                      {!categoriesData &&
                        [...Array(numberOfMainTabs)].map((count, categoriesDataIndex) => (
                          <li
                            className="canvas-categories-list__item"
                            key={'category-' + categoriesDataIndex}
                          >
                            {categoryIndex !== categoriesDataIndex + 1 && (
                              <button
                                to={`/canvases/${data.id}/categories/${categoriesDataIndex + 1
                                  }`}
                                className="canvas-categories-list__link"
                                id={`canvas-category-list-link--${categoriesDataIndex}`}
                                onClick={saveBeforeNavigation}
                              >
                                {categoriesData && (
                                  <>
                                    {categoriesData[categoriesDataIndex].categoryName !== "Optional" && (
                                      <>
                                        {`${categoriesData[categoriesDataIndex].categoryName}`}
                                      </>
                                    )}
                                    {categoriesData[categoriesDataIndex].categoryName === "Optional" && (
                                      <>
                                        {`Personalised Topic`}
                                      </>
                                    )}
                                  </>
                                )}

                                {!categoriesData && (
                                  <>
                                    {`Topic ${categoriesDataIndex + 1}`}
                                  </>
                                )}

                                <span className="canvas-categories-list__icon canvas-categories-list__icon--deselected util--hide-icon">
                                  <ChevronUp size={12} />
                                </span>
                              </button>
                            )}

                            {categoryIndex === categoriesDataIndex + 1 && (
                              <span className="canvas-categories-list__title">
                                {categoriesData && (
                                  <>
                                    {categoriesData[categoriesDataIndex].categoryName !== "Optional" && (
                                      <>
                                        {`${categoriesData[categoriesDataIndex].categoryName}`}
                                      </>
                                    )}
                                    {categoriesData[categoriesDataIndex].categoryName === "Optional" && (
                                      <>
                                        {`Personalised Topic`}
                                      </>
                                    )}
                                  </>
                                )}

                                {!categoriesData && (
                                  <>
                                    {`Topic ${categoriesDataIndex + 1}`}
                                  </>
                                )}

                                <span className="canvas-categories-list__icon canvas-categories-list__icon--selected  util--hide-icon"
                                  id={`canvas-category-selected-list-link--${categoriesDataIndex}`}
                                >
                                  <ChevronDown size={12} />
                                </span>
                              </span>
                            )}
                          </li>
                        ))}

                      {categoriesData && (
                        <>
                          {categoriesData.map((category, categoriesDataIndex) => {
                            return (
                              <li
                                className="canvas-categories-list__item"
                                key={'category-' + category.categoryId}
                              >
                                {categoryIndex !== categoriesDataIndex + 1 && (
                                  <button
                                    to={`/canvases/${data.id}/categories/${categoriesDataIndex + 1
                                      }`}
                                    className="canvas-categories-list__link"
                                    id={`canvas-category-list-link--${categoriesDataIndex}`}
                                    onClick={saveBeforeNavigation}
                                  >
                                    {categoriesData && (
                                      <>
                                        {categoriesData[categoriesDataIndex].categoryName !== "Optional" && (
                                          <>
                                            {`${categoriesData[categoriesDataIndex].categoryName}`}
                                          </>
                                        )}
                                        {categoriesData[categoriesDataIndex].categoryName === "Optional" && (
                                          <>
                                            {`Personalised Topic`}
                                          </>
                                        )}
                                      </>
                                    )}

                                    <span className="canvas-categories-list__icon canvas-categories-list__icon--deselected  util--hide-icon">
                                      <ChevronUp size={12} />
                                    </span>
                                  </button>
                                )}

                                {categoryIndex === categoriesDataIndex + 1 && (
                                  <span className="canvas-categories-list__title">
                                    {categoriesData && (
                                      <>
                                        {categoriesData[categoriesDataIndex].categoryName !== "Optional" && (
                                          <>
                                            {`${categoriesData[categoriesDataIndex].categoryName}`}
                                          </>
                                        )}
                                        {categoriesData[categoriesDataIndex].categoryName === "Optional" && (
                                          <>
                                            {`Personalised Topic`}
                                          </>
                                        )}
                                      </>
                                    )}

                                    <span className="canvas-categories-list__icon canvas-categories-list__icon--selected  util--hide-icon"
                                      id={`canvas-category-selected-list-link--${categoriesDataIndex}`}>
                                      <ChevronDown size={12} />
                                    </span>
                                  </span>
                                )}
                              </li>
                            );
                          })}
                        </>
                      )}

                      {[...Array(extraTabs)].map((count, extraTabsIndex) => {
                        let extraCategoriesDataIndex = extraTabsIndex + (categoriesData ? categoriesData.length : numberOfMainTabs);
                        return (
                          <>
                            <li
                              className="canvas-categories-list__item"
                              key={'category-' + extraCategoriesDataIndex}
                            >
                              {categoryIndex !== extraCategoriesDataIndex + 1 && (
                                <button
                                  to={`/canvases/${data.id}/categories/${extraCategoriesDataIndex + 1
                                    }`}
                                  className="canvas-categories-list__link"
                                  id={`canvas-category-list-link--${extraCategoriesDataIndex}`}
                                  onClick={saveBeforeNavigation}
                                >
                                  {`Extra Topic`}
                                  <span className="canvas-categories-list__icon canvas-categories-list__icon--deselected util--hide-icon">
                                    <ChevronUp size={12} />
                                  </span>
                                </button>
                              )}

                              {categoryIndex === extraCategoriesDataIndex + 1 && (
                                <span className="canvas-categories-list__title">
                                  {`Extra Topic`}
                                  <span className="canvas-categories-list__icon canvas-categories-list__icon--selected  util--hide-icon"
                                    id={`canvas-category-selected-list-link--${extraCategoriesDataIndex}`}>
                                    <ChevronDown size={12} />
                                  </span>
                                </span>
                              )}
                            </li>

                            {submissionUnlocked && (
                              <button
                                className="canvas-extra-topic__delete-button"
                                id="canvas-extra-topic__delete-button"
                                onClick={(e) => { handleOpenDeleteTabModal(e, extraCategoriesDataIndex + 1) }}
                                data-tab-count={extraCategoriesDataIndex + 1}
                              >
                                <X
                                  size={30}
                                  className="canvas-extra-topic__icon"
                                />
                              </button>
                            )}

                            <Modal show={deleteTabModal === (extraCategoriesDataIndex + 1)}
                              style={{ paddingTop: "20px" }}
                              id={extraCategoriesDataIndex + 1}
                            >
                              <Modal.Header style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                                <Modal.Title style={{ flex: 1, textAlign: 'center', margin: 0 }}><b>Delete Topic Tab</b></Modal.Title>
                                <button
                                  type="button"
                                  aria-label="Close"
                                  onClick={handleCloseDeleteTabModal}
                                  style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#333' }}
                                >
                                  ×
                                </button>
                              </Modal.Header>
                              <Modal.Body>Are you sure you want to delete this extra topic?</Modal.Body>
                              <Modal.Footer style={{ display: 'flex', justifyContent: 'center', gap: '1rem' }}>
                                <button className="page-content__button--medium page-content__button--secondary" onClick={(e) => { handleCloseDeleteTabModal(e) }}>
                                  Close
                                </button>
                                <button
                                  onClick={removeTopicTab}
                                  className="page-content__button--medium page-content__button--danger"
                                  onMouseEnter={handleMouseEnter}
                                  onMouseLeave={handleMouseLeave}
                                  data-tab-count={extraCategoriesDataIndex + 1}
                                >
                                  Delete
                                </button>
                              </Modal.Footer>
                            </Modal>
                          </>
                        )
                      })}

                      {submissionUnlocked &&
                        (extraTabs < extraTabsMaxLimit) && (
                          <button
                            className="canvas-categories-list__link"
                            id={`canvas-category-list-link--add-new-category`}
                            onClick={addTopicTab}
                          >
                            <Plus size={22} />
                          </button>
                        )}
                    </ul>
                  </Cell>
                </Grid>
              </div>

              <Alert variant="info" show={saveAlert} onClose={handleCloseSaveAlert} dismissible>
                <Alert.Heading><b>Save Complete</b></Alert.Heading>
                <p>
                  This canvas has now been saved. You can now close this page safely and return to this canvas later.
                </p>
              </Alert>

              {canvasData && (
                <div className="reactflow-wrap">
                  <ReactFlowProvider>
                    <div className="reactflow" ref={reactFlowWrapper}>
                      <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        onReconnect={onReconnect}
                        onNodeClick={onNodeClick}
                        onInit={onInit}
                        onDrop={onDrop}
                        onDragOver={onDragOver}
                        nodeTypes={nodeTypes}
                        snapToGrid={true}
                        snapGrid={snapGrid}
                        defaultViewport={defaultViewport}
                        nodesConnectable={submissionUnlocked}
                        edgesReconnectable={submissionUnlocked}
                        nodesDraggable={submissionUnlocked}
                        elementsSelectable={submissionUnlocked}
                        edgeTypes={edgeTypes}
                        defaultEdgeOptions={defaultEdgeOptions}
                        connectionLineComponent={CustomLine}
                        connectionMode="loose"
                        fitView
                        onEdgeMouseEnter={(e, edge) => handleEdgeMouseEnter(e, edge)}
                        onEdgeMouseLeave={(e, edge) => handleEdgeMouseLeave(e, edge)}
                        isValidConnection={isValidConnection}
                        zoomOnScroll={false}
                        panOnScroll={false}
                        onWheel={handleScrollWheel}
                        preventScrolling={zoomToggle}
                      >
                        <Controls showInteractive={submissionUnlocked} />
                        <MiniMap
                          nodeColor={nodeColor}
                          nodeStrokeWidth={3}
                          zoomable
                          pannable
                        />
                      </ReactFlow>
                    </div>
                    <Sidebar submissionUnlocked={submissionUnlocked} />
                  </ReactFlowProvider>
                </div>
              )}
            </div>
          )}

          {submissionUnlocked && (
            <div
              className={`grid-container ${controlsAndActionsButtonClassName}`}
              ref={controlsAndActionsButtonWrapper}
            >
              <Grid className="grid-padding-x align-justify">
                <Cell small={12}>
                  <div className="canvas-category-footer-wrap canvas-category-footer-wrap--top">
                    <span className="canvas-category-footer-wrap__title">Next steps:</span>
                    <HashLink
                      to="#canvas-controls-and-actions-form"
                      className="page-content__button"
                      onClick={toggleControlsAndActions}
                    >
                      Add controls and actions for this topic
                    </HashLink>
                  </div>
                </Cell>
              </Grid>
            </div>
          )}
          <div
            className={`grid-container ${submissionUnlocked ? controlsAndActionsAreaClassName : ''
              }`}
            ref={controlsAndActionsAreaWrapper}
            id="canvas-controls-and-actions-form"
          >
            <Grid className="grid-padding-x">
              <Cell small={12}>
                <div className="canvas-category-controls-and-actions">
                  <div className="canvas-category-controls-and-actions-header">
                    <h2 className="canvas-category-controls-and-actions-header__title">
                      <strong className="canvas-category-controls-and-actions-header__emphasis canvas-category-controls-and-actions-header__emphasis--controls">
                        Controls{' '}
                      </strong>{' '}
                      and{' '}
                      <strong className="canvas-category-controls-and-actions-header__emphasis canvas-category-controls-and-actions-header__emphasis--actions">
                        Actions{' '}
                      </strong>{' '}
                      for Topic {categoryIndex}
                      {categoryData?.canShareWithManager && (
                        <>: {categoryData.title}</>
                      )}
                    </h2>
                  </div>
                </div>

                {!submissionUnlocked && (
                  <div>
                    <h2 className='canvas-category-controls-and-actions-header__actions-instructions'>
                      Here are some instructions for what to do with the action checkboxes
                    </h2>
                  </div>
                )}

                {[...Array(controlsCount)].map((count, controlsIndex) => (
                  <Grid
                    className="canvas-category-controls-and-actions-section-wrap"
                    key={`controls-count-${controlsIndex}`}
                  >
                    {inputs['canvas-category-controls--' + controlsIndex] !== '' && (
                      <>
                        <Cell
                          small={12}
                          large={6}
                          className={width > 1024 ?
                            "canvas-category-controls-and-actions-section canvas-category-controls-and-actions-section--left" :
                            "canvas-category-controls-and-actions-section"}
                        >
                          <div className="canvas-category-controls-and-actions-section__inner">
                            <div className="canvas-category-controls-and-actions-section-item">
                              <div className="canvas-category-controls-and-actions-section-item__title canvas-category-controls-and-actions-section-item__title--control">
                                <span
                                  data-tooltip-id="control-tooltip"
                                  data-tooltip-place="top"
                                >
                                  Control{' '}
                                  <span className="canvas-category-control-header-info-icon canvas-category-control-header-info-icon--white" title="Click for more information about Control">
                                    <FaInfoCircle size={16} />
                                  </span>
                                </span>

                                {submissionUnlocked && (
                                  <>
                                    <button
                                      className="canvas-category-controls-and-actions-section-item__delete-button"
                                      id="canvas-category-controls-and-actions-section-item__delete-button"
                                      onClick={(e) => { handleOpenDeleteControlModal(e, controlsIndex) }}
                                      data-controls-count={controlsIndex}
                                      data-actions-count={actionsCount[controlsIndex]}
                                    >
                                      X
                                    </button>

                                    <Modal show={deleteControlModal === controlsIndex} style={{ paddingTop: "20px" }} id={controlsIndex}
                                      onHide={handleCloseDeleteControlModal}
                                      centered
                                    >
                                      <div className="modal-content" style={{ borderRadius: 8, overflow: 'hidden' }}>
                                        <div style={{ position: 'relative', padding: '0 24px 0 24px' }}>
                                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', minHeight: 56, borderBottom: '1px solid #e5e5e5', padding: '16px 0' }}>
                                            <div style={{ flex: 1, textAlign: 'center', fontWeight: 700, fontSize: 20 }}>
                                              Delete Control
                                            </div>
                                            <button
                                              type="button"
                                              aria-label="Close"
                                              onClick={handleCloseDeleteControlModal}
                                              style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', lineHeight: 1 }}
                                            >
                                              &times;
                                            </button>
                                          </div>
                                          <div style={{ padding: '10px 0', textAlign: 'center' }}>
                                            <p>Are you sure you want to delete this control?</p>
                                          </div>
                                          <div className="mb-3" style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
                                            <button
                                              onClick={handleCloseDeleteControlModal}
                                              className="page-content__button--medium page-content__button--secondary"
                                              type="button"
                                            >
                                              Cancel
                                            </button>
                                            <button
                                              onClick={removeControl}
                                              className="page-content__button--medium page-content__button--danger"
                                              onMouseEnter={handleMouseEnter}
                                              onMouseLeave={handleMouseLeave}
                                              data-controls-count={controlsIndex}
                                              data-actions-count={actionsCount[controlsIndex]}
                                              type="button"
                                            >
                                              Delete
                                            </button>
                                          </div>
                                        </div>
                                      </div>
                                    </Modal>
                                  </>
                                )}
                              </div>
                              <textarea
                                className="canvas-category-controls-and-actions-section-item__input"
                                id={`canvas-category-controls--${controlsIndex}`}
                                name={`canvas-category-controls--${controlsIndex}`}
                                defaultValue={
                                  inputs[`canvas-category-controls--${controlsIndex}`] || ''
                                }
                                onChange={handleChange}
                                rows={5}
                                readOnly={!submissionUnlocked}
                              ></textarea>
                            </div>
                          </div>
                        </Cell>
                        <Cell
                          className={width > 1024 ?
                            "canvas-category-controls-and-actions-section canvas-category-controls-and-actions-section--right" :
                            "canvas-category-controls-and-actions-section"}
                          small={12}
                          large={6}
                        >
                          <div className="canvas-category-controls-and-actions-section__inner">
                            <div className="canvas-category-controls-and-actions-section-item">
                              {[...Array(
                                actionsCount[controlsIndex] > 0
                                  ? actionsCount[controlsIndex]
                                  : 1,
                              )].map((count, actionsIndex) => (
                                <div
                                  key={`controls-count-${controlsIndex}--${actionsIndex}`}
                                >
                                  {inputs[
                                    'canvas-category-actions--' +
                                    controlsIndex +
                                    '-' +
                                    actionsIndex
                                  ] !== '' && (
                                      <>
                                        <div className="canvas-category-controls-and-actions-section-item__title canvas-category-controls-and-actions-section-item__title--action" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                          <span
                                            data-tooltip-id="action-tooltip"
                                            data-tooltip-place="top"
                                          >
                                            Action{' '}
                                            <span className="canvas-category-action-header-info-icon canvas-category-action-header-info-icon--white" title="Click for more information about Action">
                                              <FaInfoCircle size={16} />
                                            </span>
                                          </span>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            {!submissionUnlocked && (
                                              <label
                                                className='custom-checkbox canvas-actions-checkbox'
                                              >
                                                <input
                                                  className="basic-form__input"
                                                  type="checkbox"
                                                  data-control-action-index={`${controlsIndex}-${actionsIndex}`}
                                                  id={`checkbox--canvas-category-actions--${controlsIndex}-${actionsIndex}`}
                                                  name={`checkbox--canvas-category-actions--${controlsIndex}-${actionsIndex}`}
                                                  checked={
                                                    inputs[
                                                    `checkbox--canvas-category-actions--${controlsIndex}-${actionsIndex}`
                                                    ] || ''
                                                  }
                                                  onChange={handleChange}
                                                />
                                                <span className="checkmark"></span>
                                                {''}
                                              </label>
                                            )}
                                            {submissionUnlocked && (
                                              <button
                                                className="canvas-category-controls-and-actions-section-item__delete-button"
                                                id="canvas-category-controls-and-actions-section-item__delete-button"
                                                onClick={(e) => handleOpenDeleteActionModal(e, controlsIndex + "+" + actionsIndex)}
                                                data-controls-count={controlsIndex}
                                                data-actions-count={actionsIndex}
                                              >
                                                X
                                              </button>
                                            )}
                                          </div>
                                        </div>

                                        <Modal show={deleteActionModal === (controlsIndex + "+" + actionsIndex)}
                                          style={{ paddingTop: "20px" }}
                                          id={controlsIndex + "+" + actionsIndex}
                                          onHide={handleCloseDeleteActionModal}
                                          centered
                                        >
                                          <div className="modal-content" style={{ borderRadius: 8, overflow: 'hidden' }}>
                                            <div style={{ position: 'relative', padding: '0 24px 0 24px' }}>
                                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', minHeight: 56, borderBottom: '1px solid #e5e5e5', padding: '16px 0' }}>
                                                <div style={{ flex: 1, textAlign: 'center', fontWeight: 700, fontSize: 20 }}>
                                                  Delete Action
                                                </div>
                                                <button
                                                  type="button"
                                                  aria-label="Close"
                                                  onClick={handleCloseDeleteActionModal}
                                                  style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', lineHeight: 1 }}
                                                >
                                                  &times;
                                                </button>
                                              </div>
                                              <div style={{ padding: '10px 0', textAlign: 'center' }}>
                                                <p>Are you sure you want to delete this action?</p>
                                              </div>
                                              <div className="mb-3" style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
                                                <button
                                                  onClick={handleCloseDeleteActionModal}
                                                  className="page-content__button--medium page-content__button--secondary"
                                                  type="button"
                                                >
                                                  Cancel
                                                </button>
                                                <button
                                                  onClick={removeAction}
                                                  className="page-content__button--medium page-content__button--danger"
                                                  onMouseEnter={handleMouseEnter}
                                                  onMouseLeave={handleMouseLeave}
                                                  data-controls-count={controlsIndex}
                                                  data-actions-count={actionsIndex}
                                                  type="button"
                                                >
                                                  Delete
                                                </button>
                                              </div>
                                            </div>
                                          </div>
                                        </Modal>
                                        <textarea
                                          className="canvas-category-controls-and-actions-section-item__input canvas-category-controls-and-actions-section-item__input--action"
                                          id={`canvas-category-actions--${controlsIndex}-${actionsIndex}`}
                                          name={`canvas-category-actions--${controlsIndex}-${actionsIndex}`}
                                          onChange={handleChange}
                                          value={
                                            inputs[
                                            `canvas-category-actions--${controlsIndex}-${actionsIndex}`
                                            ] || ''
                                          }
                                          rows={5}
                                          readOnly={!submissionUnlocked}
                                        ></textarea>
                                      </>
                                    )}
                                </div>
                              ))}
                              {submissionUnlocked && (
                                <div className="canvas-category-controls-and-actions-section-button-wrap">
                                  <button
                                    className="canvas-category-controls-and-actions-section-button canvas-category-controls-and-actions-section-button--action"
                                    id="canvas-category-controls-and-actions-section-add-button"
                                    onClick={addAction}
                                    data-controls-count={controlsIndex}
                                  >
                                    Add another action{''}
                                    <span className="canvas-category-controls-and-actions-section-button__icon">
                                      <PlusCircle size={16}
                                        data-controls-count={controlsIndex}
                                      />
                                    </span>
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </Cell>
                      </>
                    )}
                  </Grid>
                ))}

                <Tooltip id="control-tooltip" />
                <Tooltip id="action-tooltip" />

                {submissionUnlocked && (
                  <Grid className="canvas-category-controls-and-actions-section-wrap text-right">
                    <Cell small={12}>
                      <div className="canvas-category-controls-and-actions-section-button-wrap">
                        <button
                          className="canvas-category-controls-and-actions-section-button canvas-category-controls-and-actions-section-button--control"
                          id="canvas-category-controls-and-actions-section-add-button"
                          onClick={addControl}
                        >
                          Add another control{''}
                          <span className="canvas-category-controls-and-actions-section-button__icon">
                            <PlusCircle size={16} />
                          </span>
                        </button>
                      </div>

                      <div className="page-content">
                        <button
                          className="page-content__button--medium bottom-later-button"
                          id="canvas-category-controls-and-actions-section--save"
                          onClick={onSave}
                        >
                          Save for later{''}
                          <span className="page-content__button-icon">
                            <Save size={16} />
                          </span>
                        </button>
                      </div>
                    </Cell>
                  </Grid>
                )}
              </Cell>
            </Grid>

            {!newRecord && data && submissionUnlocked && (
              <div className="canvas-category-footer-wrap">
                <Grid className="grid-padding-x">
                  <Cell small={12} large={9}>
                    <p>
                      When you have completed your conversation, you can mark the conversation
                      as complete. This will lock the content so it can no longer be edited but
                      you will be able to return to review the content and download as a PDF.
                    </p>
                  </Cell>
                  <Cell small={12} large={3} className="text-right bottom-finish-button-container">

                    <Link
                      to={`/canvases/${data.id}/confirmations/new`}
                      id="page-navigation-button--complete"
                      className="page-content__button--medium bottom-finish-button"
                      onClick={saveAndCompleteConversation}
                    >
                      Save and finish{''}
                      <span className="canvas-categories-navigation-list__icon">
                        <Check2Circle size={14} />
                      </span>
                    </Link>
                  </Cell>
                </Grid>
              </div>
            )}
          </div>
        </>
      )}
      <Modal show={showExitModal} onHide={handleCancelExit} backdrop="static" keyboard={false} centered>
        <div className="modal-content" style={{ borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ position: 'relative', padding: '0 24px 0 24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', minHeight: 56, borderBottom: '1px solid #e5e5e5', padding: '16px 0' }}>
              <div style={{ flex: 1, textAlign: 'center', fontWeight: 700, fontSize: 20 }}>
                Unsaved changes
              </div>
              <button
                type="button"
                aria-label="Close"
                onClick={handleCancelExit}
                style={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', lineHeight: 1 }}
              >
                &times;
              </button>
            </div>
            <div style={{ padding: '10px 0', textAlign: 'center' }}>
              <p>You have unsaved changes. Do you want to save them before leaving this page?</p>
            </div>
            <div className="mb-3" style={{ display: 'flex', justifyContent: 'center', gap: 16 }}>
              <button
                onClick={handleDiscardAndContinue}
                className="page-content__button--medium page-content__button--danger"
                type="button"
              >
                Leave without saving
              </button>
              <button
                onClick={handleSaveAndContinue}
                className="page-content__button--medium page-content__button--secondary"
                type="button"
              >
                Save and continue
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
